import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
}

/** Detect Android WebView where WebWorkers and certain APIs can fail */
function isWebView(): boolean {
  const ua = navigator.userAgent || '';
  // Match "wv" token at word boundary, "; wv)" in user agent, or "WebView" string
  return /wv\b/.test(ua) || /; wv\)/.test(ua) || /WebView/.test(ua);
}

export const DEFAULT_COMPRESSION_OPTIONS: CompressionOptions = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

/**
 * Normalize a value to a proper File object.
 * In Android WebView, file picker can return Blob-like objects that aren't
 * true File instances. This function ensures we always have a valid File.
 */
function ensureFile(input: File | Blob, fallbackName = 'image.jpg'): File {
  if (input instanceof File && input.name && input.size > 0) {
    return input;
  }

  // Wrap Blob or broken File into a proper File object
  const name = input instanceof File ? (input.name || fallbackName) : fallbackName;
  const type = input.type || 'image/jpeg';
  return new File([input], name, { type, lastModified: Date.now() });
}

export async function compressImage(
  file: File,
  options: CompressionOptions = DEFAULT_COMPRESSION_OPTIONS
): Promise<File> {
  console.log('[Compress] 📷 Input file:', file.name, file.size, file.type);

  // Normalize the file first (important for WebView)
  file = ensureFile(file);

  if (!file.type.startsWith('image/')) {
    throw new Error('File phải là hình ảnh');
  }

  if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
    throw new Error('Chỉ chấp nhận file JPEG hoặc PNG');
  }

  // Guard against empty files (common in WebView file picker)
  if (file.size === 0) {
    throw new Error('Ảnh bị lỗi hoặc rỗng (0 bytes)');
  }

  if (file.size <= (options.maxSizeMB || 2) * 1024 * 1024) {
    console.log('[Compress] ⏭️ File already small enough, skipping compression');
    return file;
  }

  try {
    // Disable WebWorker in WebView environments where they frequently fail
    const inWebView = isWebView();
    if (inWebView) {
      console.log('[Compress] 📱 WebView detected, disabling WebWorker');
    }

    const compressedFile = await imageCompression(file, {
      maxSizeMB: options.maxSizeMB || 2,
      maxWidthOrHeight: options.maxWidthOrHeight || 1920,
      useWebWorker: inWebView ? false : (options.useWebWorker !== false),
    });

    const result = new File([compressedFile], file.name, {
      type: compressedFile.type,
      lastModified: Date.now(),
    });

    console.log('[Compress] ✅ Result:', result.name, result.size, result.type);
    return result;
  } catch (error) {
    console.error('[Compress] ❌ Failed:', error);
    // In WebView, if compression fails, return the original file as fallback
    // instead of crashing the entire upload flow
    const inWebView = isWebView();
    if (inWebView) {
      console.warn('[Compress] 📱 WebView fallback: returning original file');
      return file;
    }
    throw new Error(`Nén ảnh thất bại: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export async function compressImages(files: File[]): Promise<File[]> {
  return Promise.all(files.map(file => compressImage(file)));
}
